import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, from, switchMap, map, catchError, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Holiday, HolidayFilters } from '../models/holiday.model';
import { ApiPaginatedResponse } from '@app/service/pagination.service';
import { DataService } from '@app/service/data.service';
import { IsoString } from '@app/shared/type-aliases/iso-string';
import { response } from 'express';

export interface NextBusinessDayResponse {
  date: IsoString;
  daysUntilNextBusinessDay: number;
  isBusinessDay: boolean;
  nextBusinessDay: IsoString | null;
}

/** Cache de feriados por ano para evitar requisições repetidas */
interface HolidayCache {
  [year: number]: {
    holidays: Holiday[];
    timestamp: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class HolidayService {
  private readonly API_URL = `${environment.mkgoURL}/api/v1/holidays`;
  private _isFromMatrix = false;
  
  private _holidayCache: HolidayCache = {};
  private readonly CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutos

  constructor(
    private http: HttpClient,
    private dataService: DataService
  ) { }

  getPaginated(filters: HolidayFilters): Observable<ApiPaginatedResponse<Holiday>> {
    let params = new HttpParams()
      .set('page', (filters.page || 1).toString())
      .set('limit', (filters.limit || 10).toString())
      .set('all', (filters.all || 0).toString());

    if (filters.search) {
      params = params.set('search', filters.search);
    }
    if (filters.month) {
      params = params.set('month', filters.month);
    }
    if (filters.year) {
      params = params.set('year', filters.year.toString());
    }
    if (filters.type) {
      params = params.set('type', filters.type.toString());
    }
    if (filters.inactives !== undefined) {
      params = params.set('inactives', filters.inactives.toString());
    }
    if (filters.startDate) {
      params = params.set('startDate', new Date(filters.startDate).toISOString());
    }
    if (filters.endDate) {
      params = params.set('endDate', new Date(filters.endDate).toISOString());
    }
    console.log(filters)

    return from(firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))).pipe(
      switchMap(header => this.http.get<ApiPaginatedResponse<Holiday>>(this.API_URL, { params, headers: header.headers }))
    );
  }

  getById(id: string): Observable<Holiday> {
    return from(firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))).pipe(
      switchMap(header => this.http.get<Holiday>(`${this.API_URL}/${id}`, header))
    );
  }

  create(holiday: Partial<Holiday>): Observable<Holiday> {
    return from(firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))).pipe(
      switchMap(header => this.http.post<Holiday>(this.API_URL, holiday, header))
    );
  }

  update(id: string, holiday: Partial<Holiday>): Observable<Holiday> {
    return from(firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))).pipe(
      switchMap(header => this.http.put<Holiday>(`${this.API_URL}/${id}`, holiday, header))
    );
  }

  inactiveOrActive(id: string, action: 'disable' | 'active'): Observable<Holiday> {
    return from(firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))).pipe(
      switchMap(header => this.http.patch<Holiday>(`${this.API_URL}/${id}/status/${action}`, {}, header))
    );
  }


  delete(id: string): Observable<void> {
    return from(firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))).pipe(
      switchMap(header => this.http.delete<void>(`${this.API_URL}/${id}`, header))
    );
  }

  isHoliday(date: Date | string): Observable<{ isHoliday: boolean; holiday?: Holiday }> {
    const dateStr = new Date(date).toISOString();
    return from(firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))).pipe(
      switchMap(header => this.http.get<{ isHoliday: boolean; holiday?: Holiday }>(`${this.API_URL}/check/${dateStr}`, header))
    );
  }

  getNextBusinessDay(date: Date | string): Observable<NextBusinessDayResponse> {
    const dateStr = new Date(date).toISOString();
    return this.dataService.httpOptions(this._isFromMatrix).pipe(
      switchMap(header => this.http.get<NextBusinessDayResponse>(`${this.API_URL}/business-day/${dateStr}`, header)),
      map(response => {
        if (response.nextBusinessDay) {
          const utcDate = new Date(response.nextBusinessDay);
          const localDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate(), 12, 0, 0, 0);
          response.nextBusinessDay = localDate.toISOString();
        }
        return response;
      }),
      catchError(_error => of({ 
        date: dateStr,
        daysUntilNextBusinessDay: 0,
        isBusinessDay: false,
        nextBusinessDay: null
      }))
    );
  }

  getByPeriod(startDate: Date | string, endDate: Date | string): Observable<Holiday[]> {
    let params = new HttpParams()
      .set('startDate', new Date(startDate).toISOString())
      .set('endDate', new Date(endDate).toISOString());

    return from(firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))).pipe(
      switchMap(header => this.http.get<Holiday[]>(`${this.API_URL}/period`, { params, headers: header.headers }))
    );
  }

  /**
   * Busca feriados do ano com cache otimizado.
   * Ideal para usar no datepicker para destacar feriados.
   * @param year Ano para buscar feriados
   * @param forceRefresh Se true, ignora o cache e busca novamente
   */
  getByYear(year: number, forceRefresh = false): Observable<Holiday[]> {
    const cached = this._holidayCache[year];
    const now = Date.now();
    
    if (!forceRefresh && cached && (now - cached.timestamp) < this.CACHE_DURATION_MS) {
      return of(cached.holidays);
    }

    return from(firstValueFrom(this.dataService.httpOptions(this._isFromMatrix))).pipe(
      switchMap(header => this.http.get<Holiday[]>(`${this.API_URL}/year-holidays/${year}`, { headers: header.headers })),
      tap(holidays => {
        this._holidayCache[year] = {
          holidays,
          timestamp: now
        };
      })
    );
  }

  clearCache(): void {
    this._holidayCache = {};
  }
}
